import { recapOutcome } from './recapOutcome';

import type { MeetingTranscriptLabels } from './types';
import type { MeetingRecap } from '@/types/review/PostReviewMeeting';

import { getLocalizedText } from '@/utils/localization';

/**
 * The recap as a plain-text file, in the reader's language.
 *
 * Shared by both journeys rather than written twice: the recap has one shape, and two renderings of
 * it would drift the day one of them gains a section. The wording comes from the caller's labels, so
 * each journey keeps its own.
 *
 * A section with no item is left out entirely — a heading standing over nothing reads as data that
 * failed to arrive rather than as data that does not exist.
 */
export const recapToPlainText = (recap: MeetingRecap, labels: MeetingTranscriptLabels): string => {
    const lines: string[] = [getLocalizedText(recap.essential), ''];
    // A recap written from only part of the meeting says so in the file too, as it does on the screen.
    if (recapOutcome(recap) === 'incomplete') {
        lines.push(labels.disclosure, '');
    }

    const section = (heading: string, items: string[]) => {
        if (items.length === 0) {
            return;
        }
        lines.push(heading, ...items.map((item) => `- ${item}`), '');
    };

    section(
        labels.discussedPoints,
        recap.discussedPoints.map((point) => getLocalizedText(point)),
    );
    section(
        labels.decisions,
        recap.decisions.map((decision) => getLocalizedText(decision)),
    );
    section(
        labels.commitments,
        recap.commitments.map((commitment) => {
            const due = getLocalizedText(commitment.dueDate);
            const owner = labels.commitmentOwner(commitment.owner);
            return `${getLocalizedText(commitment.action)} — ${owner}${due ? ` (${due})` : ''}`;
        }),
    );
    section(
        labels.followUps,
        recap.followUps.map((followUp) => getLocalizedText(followUp)),
    );

    return lines.join('\n');
};
